import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Divider, Group, Space, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconBox, IconBuildingStore, IconClipboardPlus, IconMapPin, IconTag, IconUser, IconUsers } from "@tabler/icons-react";
import moment from "moment";

import { EventDto, EventSource, EventTarget, EventType } from "../../models/event.models";
import { getEventSourceType, getEventTargetIcon } from "../../functions/event.functions";
import { getWorkOrderStatusDescription } from "../../functions/workorder.functions";

import classes from "./EventList.module.css"

export interface EventListProps {
	events: EventDto[];
	commentsOnly?: boolean;
	showSource?: boolean;
	showDividers?: boolean;
}

export const EventList = ({ events, commentsOnly = false, showSource = false, showDividers = false }: EventListProps) => {

	const filteredEvents = commentsOnly ? events.filter(e => e.EventType == EventType.Commented) : events.filter(e => e.EventType != EventType.Read && e.EventType != EventType.Commented);

	const renderWorkOrderEvent = (event: EventDto) => {
		if (event.TargetType != EventTarget.WorkOrder) return null;

		return <>
			<Group gap={5}>
				<Text fz="sm">Assigned the</Text>
				<Group gap={0} className={classes.link}>
					<IconClipboardPlus size={14} />
					<Link to={`/workorders/${event.TargetId}`}>Work Order #{event.TargetDescription}</Link>
				</Group>
				<Text fz="sm">.</Text>
			</Group>
		</>;
	};

	const renderLocationEvent = (event: EventDto) => {
		if (event.TargetType != EventTarget.Location) return null;

		return <>
			<Group gap={5}>
				<Text fz="sm">Assigned the</Text>
				<Group gap={3} className={classes.link}>
					<IconMapPin size={14} />
					<Link to={`/locations/${event.TargetId}`}>{event.TargetDescription}</Link>
				</Group>
				<Text fz="sm">location.</Text>
			</Group>
		</>;
	};

	const renderCategoryEvent = (event: EventDto) => {
		if (event.TargetType != EventTarget.Category) return null;

		return <>
			<Group gap={5}>
				<Text fz="sm">Assigned the</Text>
				<Group gap={3} className={classes.link}>
					<IconTag size={14} />
					<Link to={`/categories/${event.TargetId}`}>{event.TargetDescription}</Link>
				</Group>
				<Text fz="sm">category.</Text>
			</Group>
		</>;
	};

	const renderAssetEvent = (event: EventDto) => {
		if (event.TargetType != EventTarget.Asset) return null;

		return <>
			<Group gap={5}>
				<Text fz="sm">Assigned the</Text>
				<Group gap={3} className={classes.link}>
					<IconBox size={14} />
					<Link to={`/assets/${event.TargetId}`}>{event.TargetDescription}</Link>
				</Group>
				<Text fz="sm">asset.</Text>
			</Group>
		</>;
	};

	const renderVendorEvent = (event: EventDto) => {
		if (event.TargetType != EventTarget.Vendor) return null;

		return <>
			<Group gap={5}>
				<Text fz="sm">Assigned the</Text>
				<Group gap={3} className={classes.link}>
					<IconBuildingStore size={14} />
					<Link to={`/vendors/${event.TargetId}`}>{event.TargetDescription}</Link>
				</Group>
				<Text fz="sm">vendor.</Text>
			</Group>
		</>;
	};
	
	const renderComment = (event: EventDto) => {
		if (event.TargetType != EventTarget.Comment) return null;

		return <>
			<Text component="div" fz="sm">
				<div dangerouslySetInnerHTML={{ __html: event.TargetDescription.replaceAll("\n", "<br />") }} />
			</Text>
		</>;
	};

	const renderWorkOrderRequested = (event: EventDto) => {
		if (event.EventType != EventType.Requested) return null;

		return <>
			<Text fz="sm">Work order was requested.</Text>
		</>;
	}

	const renderWorkOrderCreated = (event: EventDto) => {
		if (event.EventType != EventType.Created) return null;

		return <>
			<Text fz="sm">Work order was created.</Text>
		</>;
	}

	const renderTitleUpdated = (event: EventDto) => {
		if (event.TargetType != EventTarget.Title) return null;

		return <>
			<Text fz="sm">Updated the title to "{event.TargetDescription}".</Text>
		</>;
	}

	const renderDescriptionUpdated = (event: EventDto) => {
		if (event.TargetType != EventTarget.Description) return null;

		return <>
			<Text fz="sm">Updated the description to "{event.TargetDescription}".</Text>
		</>;
	};

	const renderStatusUpdated = (event: EventDto) => {
		if (event.TargetType != EventTarget.Status) return null;

		return <>
			<Text fz="sm">Updated the status to {getWorkOrderStatusDescription(parseInt(event.TargetId))}.</Text>
		</>;
	};

	const renderPriorityUpdated = (event: EventDto) => {
		if (event.TargetType != EventTarget.Priority) return null;

		return <>
			<Text fz="sm">Updated the priority to {event.TargetDescription}.</Text>
		</>;
	}

	const renderStartDateUpdated = (event: EventDto) => {
		if (event.TargetType != EventTarget.StartedDate) return null;

		return <>
			<Text fz="sm">Updated the start date to {event.TargetDescription}.</Text>
		</>;
	}

	const renderDueDateUpdated = (event: EventDto) => {
		if (event.TargetType != EventTarget.DueDate) return null;

		return <>
			<Text fz="sm">Updated the due date to {event.TargetDescription}.</Text>
		</>;
	}

	const renderUserAssigned = (event: EventDto) => {
		if (event.TargetType != EventTarget.User) return null;

		return <>
			<Group gap={5}>
				<Text fz="sm">Assigned to</Text>
				<Group gap={3} className={classes.link}>
					<IconUser size={14} />
					<Link to={`/users/${event.TargetId}`}>{event.TargetDescription}</Link>
				</Group>
				<Text fz="sm">{event.TargetDescription.toLowerCase().indexOf("user") !== -1 ? "" : "user."}</Text>
			</Group>
		</>;
	}

	const renderTeamAssigned = (event: EventDto) => {
		if (event.TargetType != EventTarget.Team) return null;

		return <>
			<Group gap={5}>
				<Text fz="sm">Assigned the</Text>
				<Group gap={3} className={classes.link}>
					<IconUsers size={14} />
					<Link to={`/teams/${event.TargetId}`}>{event.TargetDescription}{event.TargetDescription.toLowerCase().indexOf("team") !== -1 ? "." : ""}</Link>
				</Group>
				<Text fz="sm">{event.TargetDescription.toLowerCase().indexOf("team") !== -1 ? "" : "team."}</Text>
			</Group>
		</>;
	}

	const renderSource = (event: EventDto) => {
		if (!showSource) return null;
		if (event.SourceType == null) return null;

		var sourcePath = '';
		switch (event.SourceType) {
			case EventSource.WorkOrder:
				sourcePath = 'workorders';
				break;
			case EventSource.Location:
				sourcePath = 'locations';
				break;
			case EventSource.Category:
				sourcePath = 'categories';
				break;
			case EventSource.User:
				sourcePath = 'users';
				break;
			case EventSource.Team:
				sourcePath = 'teams';
				break;
			default:
				return null;
		}

		return <>
			<Group gap={5}>
				<Group gap={3}>
					{/* {event.SourceType == EventSource.WorkOrder && <IconClipboardText size={14} />}
					{event.SourceType == EventSource.Location && <IconMapPin size={14} />}
					{event.SourceType == EventSource.Category && <IconTag size={14} />}
					{event.SourceType == EventSource.User && <IconUser size={14} />}
					{event.SourceType == EventSource.Team && <IconUsers size={14} />} */}
					<Text fz="sm">In {getEventSourceType(event)}:</Text>
					<Group className={classes.link}>
						<Link to={`/${sourcePath}/${event.SourceId}`}>{event.SourceId != "" ? ` ${event.SourceDescription}` : ""}</Link>
					</Group>
				</Group>
			</Group>
		</>;
	}

	return <>
		<Stack gap="xs">
			{filteredEvents.map((event, index) => <Fragment key={event.EventId}>
				{renderSource(event)}
				<Group gap="xs" justify="flex-start" align="flex-start" wrap="nowrap">
					<ThemeIcon variant="light" radius="xl" size="lg" mt={5}>
						{getEventTargetIcon(event)}
					</ThemeIcon>
					<Stack gap={0}>
						<Group gap={5} wrap="nowrap">
							<Text fz="sm" fw="bold">{event.User.Name}</Text>
							<Text fz="xs" c="dimmed">{moment.utc(event.EventDate).local().format('M/D/YYYY, h:mma')}</Text>
						</Group>
						{renderWorkOrderRequested(event)}
						{renderWorkOrderCreated(event)}
						{renderWorkOrderEvent(event)}
						{renderLocationEvent(event)}
						{renderCategoryEvent(event)}
						{renderAssetEvent(event)}
						{renderVendorEvent(event)}
						{renderComment(event)}
						{renderTitleUpdated(event)}
						{renderDescriptionUpdated(event)}
						{renderStatusUpdated(event)}
						{renderPriorityUpdated(event)}
						{renderStartDateUpdated(event)}
						{renderDueDateUpdated(event)}
						{renderUserAssigned(event)}
						{renderTeamAssigned(event)}
						<Space h="xs" />
					</Stack>
				</Group>
				{showDividers && index < filteredEvents.length - 1 && <><Divider my={5} /></>}
			</Fragment>)}

			{filteredEvents.length == 0 && <>
				<Text fz="sm" c="dimmed">No {commentsOnly ? "comments" : "recent activity"}</Text>
			</>}
		</Stack>
	</>
};